<template>
  <div>
    <BaseNotification v-if="fbError" layout="inline" type="error" class="mb-4">
      {{ fbError }}
    </BaseNotification>

    <BaseButton
      class="button--fb-new w-full mb-4"
      color="white"
      @click="facebookClick"
    >
      {{ isLoginForm && $store.getters.cta.loginWithFacebook ? $store.getters.cta.loginWithFacebook : $store.getters.cta.submitWithFacebook }}
    </BaseButton>

    <Tooltip
      :text="googleApiError"
    >
      <BaseButton
        id="modal-google-login"
        class="button--google w-full mb-6"
        color="white"
        :disabled="!!googleApiError"
        @click="googleClick"
      >
        {{ isLoginForm && $store.getters.cta.loginWithGoogle ? $store.getters.cta.loginWithGoogle : $store.getters.cta.submitWithGoogle }}
      </BaseButton>
    </Tooltip>

    <div
      id="login-with-google-container"
      class="none"
      ref="sendWithGoogle"
    />

    <div class="relative flex justify-center mb-6">
      <span class="bg-white px-4">
        {{ isLoginForm ? 'or' : 'or add your contact details' }}
      </span>
      <div class="strikethrough-message" />
    </div>

    <TextField
      v-if="!isLoginForm"
      v-model="form.fullName.value"
      :icon="UserIcon"
      :errors="form.fullName.errors"
      :is-marked-as-invalid="form.fullName.errors.length > 0"
      name="name"
      autocomplete="name"
      class="mb-4"
      placeholder="Full name"
      data-test-id="enquiry-form__name"
      @update:modelValue="updateField('fullName')"
      @click="$emit('click', 'fullName')"
    />

    <EmailField
      v-model="form.email.value"
      :errors="form.email.errors"
      name="email"
      autocomplete="email"
      data-test-id="enquiry-form__email"
      @update:modelValue="updateField('email')"
      @click="$emit('click', 'email')"
    />

    <TextField
      v-if="isLoginForm"
      v-model="form.password.value"
      :icon="LockIcon"
      :errors="aggregatedPasswordErrors"
      :is-marked-as-invalid="form.password.errors.length > 0"
      name="password"
      type="password"
      maxlength="64"
      class="mb-4"
      placeholder="Password"
      data-test-id="enquiry-form__password"
      @update:modelValue="updateField('password')"
    />
    <p v-if="isLoginForm" class="-mt-2">
      <a class="font-semibold" href="/forgot/client">Forgot your password?</a>
    </p>

    <PhoneField
      v-if="phoneFieldVisible"
      v-model="form.phone.value"
      data-test-id="enquiry-form__phone"
      name="tel"
      class="mb-4"
      :errors="form.phone.errors"
      @update:modelValue="updateField('phone')"
      @click="$emit('click', 'phone')"
    />

    <CheckboxField
      v-model="form.marketingConsent.value"
      class="items-start my-6"
      label="I'd like to receive special offers and inspiration from Tagvenue. You can opt out at any time."
      @change="trackMarketingConsentChanged"
    />
  </div>
</template>

<script>
import PhoneIcon from '@tagvenue/icons/24/line/communication/phone.svg';
import PadLockIcon from '@tagvenue/icons/24/line/security/lock-01.svg';
import UserIcon from '@tagvenue/icons/24/line/users/user-02.svg';
import { markRaw } from 'vue';

import EmailField from '@/components/Forms/EmailField.vue';
import BaseNotification from '@/components/Ui/BaseNotification.vue';
import BaseButton from '@/components/Ui/Buttons/BaseButton.vue';
import CheckboxField from '@/components/Ui/Forms/CheckboxField.vue';
import PhoneField from '@/components/Ui/Forms/PhoneField.vue';
import TextField from '@/components/Ui/Forms/TextField.vue';
import Tooltip from '@/components/Ui/Tooltip.vue';
import { trackingMapFieldToLabel } from '@/modules/public/enquiry-form/store/actions/tracking.actions.js';
import formValidation, { FORM_VALIDATION } from '@/services/formValidation/index.js';

export default {
  name: 'UserInfo',
  emits: [
    'validateEntireForm',
    'oAuthSubmit',
    'facebookLoginButtonClicked',
    'googleLoginButtonClicked',
    'click',
  ],
  mixins: [formValidation],
  components: { BaseNotification, CheckboxField, Tooltip, EmailField, TextField, BaseButton, PhoneField },

  props: {
    isFormValid: { // We don't want to trigger OAuth with invalid rest of the form
      type: Boolean,
      required: true,
    },
    isLoginForm: {
      type: Boolean,
      required: false,
      default: false,
    },
    hidePhoneNumberField: {
      type: Boolean,
      required: false,
      default: false,
    },
  },

  data() {
    const form = this.$store.state.form.userInfo;
    return {
      form: {
        fullName: {
          value: form.fullName,
          errors: [],
        },
        email: {
          value: form.email,
          errors: [],
        },
        password: {
          value: form.password,
          errors: [],
        },
        phone: {
          value: form.phone,
          errors: [],
        },
        message: {
          value: null,
          errors: [],
        },
        marketingConsent: {
          value: form.marketingConsent,
          errors: [],
        },
      },
      oauthTriggered: null,
      UserIcon: markRaw(UserIcon),
      PhoneIcon: markRaw(PhoneIcon),
      LockIcon: markRaw(PadLockIcon),
      googleApiError: '',
      fbError: '',
    };
  },

  computed: {
    aggregatedPasswordErrors() {
      if (this.form.message.errors.length > 0) {
        return [
          ...this.form.password.errors,
          this.form.message.errors, // actually not an array
        ];
      }
      return this.form.password.errors;
    },
    phoneFieldVisible() {
      if (this.isLoginForm) {
        return false;
      }

      return !this.hidePhoneNumberField;
    },
    formValidation() {
      if (this.isLoginForm) {
        return {
          email: {
            [FORM_VALIDATION.REQUIRED]: 'Please enter your email address',
            [FORM_VALIDATION.EMAIL]: 'Please enter a valid email address',
          },
          password: {
            [FORM_VALIDATION.REQUIRED]: 'Please enter your password.',
          },
        };
      }

      return {
        fullName: {
          [FORM_VALIDATION.REQUIRED]: 'Please enter your name',
          [FORM_VALIDATION.NOT_EMAIL]: 'Please enter a name instead of an email',
          [FORM_VALIDATION.MAX_LENGTH]: {
            value: 50,
            message: 'The name must not be greater than 50 characters.',
          },
        },
        email: {
          [FORM_VALIDATION.REQUIRED]: 'Please enter your email address',
          [FORM_VALIDATION.EMAIL]: 'Please enter a valid email address',
        },
      };
    },
  },

  watch: {
    form: {
      handler() {
        this.$store.commit('setUserInfoForm', this.form);
      },
      deep: true,
    },
    isFormValid(isValid) {
      if (!isValid) {
        return;
      }
      if (this.oauthTriggered === 'google') {
        this.triggerGAuth();
      } else {
        this.triggerFacebookAuth();
      }
    },
  },

  methods: {
    submit() {
      const isValid = this.validateFields();
      if (isValid) {
        this.$store.commit('setUserInfoForm', this.form);
      }
      return { isValid };
    },
    googleClick() {
      this.$emit('googleLoginButtonClicked');

      this.$store.dispatch('trackEnquiryFormNavigation', {
        label: 'send-with-google',
      });

      if (this.$store.getters.isRtbFlowPossible) {
        this.$store.dispatch('trackGA4MessageHostInteraction', {
          section: 'signup_step',
          element: 'send_with_google_button',
        });
      } else {
        this.$store.dispatch('trackGA4Interaction', {
          section: 'signup',
          element: 'google_button',
          value: null,
          interaction: 'click',
        });
      }

      if (!this.isFormValid && !this.$store.getters.isRtbFlowPossible) {
        this.oauthTriggered = 'google';
        this.$emit('validateEntireForm');
        return;
      }

      this.triggerGAuth();
    },
    triggerGAuth() {
      this.googleLoginApi.triggerModal();
    },
    async triggerFacebookAuth() {
      try {
        const { isSuccess } = await App.es6.views.login.facebookAuth.trigger({
          token: this.$store.state.api.token,
          url: this.$store.state.api.facebookAuth,
          trackingParams: this.$store.getters.authEventsTrackingParams('facebook'),
        });
        if (isSuccess) {
          this.$emit('oAuthSubmit');
          this.$store.dispatch('trackEnquiry', 'facebook');
          this.$store.commit('setSocialAuthMethod', 'facebook');
        } else {
          this.fbError = 'Facebook login: Email permissions are required. Try again or use another login method.';
        }
      } catch (e) {
        this.fbError = 'Facebook login: Email permissions are required. Try again or use another login method.';
      }
    },
    facebookClick() {
      this.$emit('facebookLoginButtonClicked');

      this.$store.dispatch('trackEnquiryFormNavigation', {
        label: 'send-with-facebook',
      });

      if (this.$store.getters.isRtbFlowPossible) {
        this.$store.dispatch('trackGA4MessageHostInteraction', {
          section: 'signup_step',
          element: 'send_with_facebook_button',
        });
      } else {
        this.$store.dispatch('trackGA4Interaction', {
          section: 'signup',
          element: 'facebook_button',
          value: null,
          interaction: 'click',
        });
      }

      if (!this.isFormValid && !this.$store.getters.isRtbFlowPossible) {
        this.oauthTriggered = 'facebook';
        this.$emit('validateEntireForm');
        return;
      }
      this.triggerFacebookAuth();
    },
    updateField(fieldName) {
      this.form[fieldName].errors = [];

      this.$store.dispatch('trackEnquiryFormValueChange', {
        label: trackingMapFieldToLabel(fieldName),
        values: this.values,
        errors: this.errors,
      });
    },
    trackMarketingConsentChanged() {
      this.$store.dispatch('trackGA4Interaction', {
        section: 'signup',
        element: 'marketing_consent',
        value: this.form.marketingConsent.value ? '1' : '0',
        interaction: 'change',
      });
    },
  },

  mounted() {
    const { gAuth } = this.$store.state.api;
    this.googleLoginApi = App.es6.views.quick_estimate.google({
      googleUrl: gAuth.url,
      googleClientId: gAuth.clientId,
      csrfToken: '',
      buttonId: 'modal-google-login',
      trackingParams: () => this.$store.getters.authEventsTrackingParams('google'),
      callback: () => {
        this.$emit('oAuthSubmit');
        this.$store.dispatch('trackEnquiry', 'google');
        this.$store.commit('setSocialAuthMethod', 'google');
      },
    }).init(() => {
      if (!window._GAPI_ERROR) {
        return;
      }
      if (window._GAPI_ERROR === 'Cookies are not enabled in current environment.') {
        this.googleApiError = 'Please open the page in non-incognito mode to proceed';
        return;
      }
      this.googleApiError = 'Google Authentication is currently unavailable. Please try again later.';
      Rollbar.error('Google analytics initialize error: ', window._GAPI_ERROR);
    });

    App.es6.views.login.facebookAuth.loadFacebookSDK();
  },
};
</script>

<style lang="scss" scoped>
@use "@sass/design-system/variables" as ds-variables;

.strikethrough-message {
  width: 100%;
  height: 1px;
  position: absolute;
  background: ds-variables.$grey-300;
  top: 50%;
  z-index: -1;
}
</style>
